import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, HStack, Text, VStack, Icon, chakra } from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FiCompass, FiUsers, FiPlus, FiClock, FiCheck, FiMessageSquare, FiBarChart2,
  FiMail, FiLink, FiSmartphone, FiGlobe, FiLock, FiChevronRight
} from "react-icons/fi";

// /vsaas — scrollytelling „Jak dołączyć do grupy", łopatologicznie, krok po kroku.
// Bez podpowiedzi/nawigacji na ekranie (user nawiguje sam). Klawiatura ↑/↓ działa cicho.
// Realne linki (lubbezpośrednio.pl, /organizator/odkrywaj), zaproszenie: link / QR / e-mail, w apce ORAZ w webie.

const MBox = chakra(motion.div);
const reveal = { hidden: { opacity: 0, y: 34 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };
const revealD = (d: number) => ({ hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut", delay: d } } });

// — pastylka z realnym linkiem (pasek adresu) —
const LinkPill: FC<{ url: string; icon?: any }> = ({ url, icon = FiGlobe }) => (
  <HStack spacing="9px" bg="white" border="1px solid rgba(67,0,152,0.15)" borderRadius="full" px="16px" py="9px" boxShadow="0 8px 22px rgba(67,0,152,0.10)">
    <Icon as={icon} color="accent" />
    <Text fontSize="14px" fontWeight="700" color="primary">{url}</Text>
  </HStack>
);

// — mock kodu QR (SVG, ilustracyjny) —
const Finder: FC<{ x: number; y: number; s: number }> = ({ x, y, s }) => (
  <>
    <rect x={x} y={y} width={s * 7} height={s * 7} fill="#430098" />
    <rect x={x + s} y={y + s} width={s * 5} height={s * 5} fill="#fff" />
    <rect x={x + s * 2} y={y + s * 2} width={s * 3} height={s * 3} fill="#430098" />
  </>
);
const QRMock: FC<{ size?: number }> = ({ size = 168 }) => {
  const n = 21, s = size / n;
  const cells: ReactNode[] = [];
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    const inFinder = (r < 7 && c < 7) || (r < 7 && c >= n - 7) || (r >= n - 7 && c < 7);
    if (inFinder) continue;
    if ((r * 7 + c * 13 + r * c) % 3 === 0) cells.push(<rect key={`${r}-${c}`} x={c * s} y={r * s} width={s} height={s} fill="#430098" />);
  }
  return (
    <Box bg="white" p="14px" borderRadius="18px" boxShadow="0 18px 44px rgba(67,0,152,0.16)" border="1px solid rgba(67,0,152,0.08)">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <rect width={size} height={size} fill="#fff" />
        {cells}
        <Finder x={0} y={0} s={s} />
        <Finder x={size - s * 7} y={0} s={s} />
        <Finder x={0} y={size - s * 7} s={s} />
      </svg>
    </Box>
  );
};

const SidebarMock: FC = () => (
  <Box w="250px" bg="white" borderRadius="18px" boxShadow="0 20px 50px rgba(67,0,152,0.18)" p="12px" border="1px solid rgba(67,0,152,0.08)">
    {[
      { label: "Pulpit", icon: FiBarChart2, active: false },
      { label: "Społeczność", icon: FiCompass, active: true },
      { label: "Ludzie", icon: FiUsers, active: false },
      { label: "Organizacje", icon: FiUsers, active: false }
    ].map((r, i) => (
      <Flex key={i} align="center" gap="11px" px="13px" py="11px" mb="4px" borderRadius="12px"
        bg={r.active ? "rgba(229,31,68,0.10)" : "transparent"} color={r.active ? "accent" : "gray.600"} fontWeight={r.active ? "800" : "600"}>
        <Icon as={r.icon} boxSize="18px" /><Text fontSize="15px">{r.label}</Text>
      </Flex>
    ))}
  </Box>
);

const GroupCard: FC<{ name: string; members: string; state?: "join" | "member"; w?: string }> = ({ name, members, state = "join", w = "230px" }) => {
  const btn = state === "join"
    ? { label: "Dołącz", bg: "accent", color: "white", icon: FiPlus }
    : { label: "Należysz", bg: "rgba(67,0,152,0.08)", color: "primary", icon: FiCheck };
  return (
    <Box w={w} bg="white" borderRadius="16px" boxShadow="0 14px 36px rgba(67,0,152,0.12)" p="15px" border="1px solid rgba(67,0,152,0.07)">
      <HStack spacing="11px" mb="11px">
        <Flex w="40px" h="40px" borderRadius="12px" bg="linear-gradient(135deg,#430098,#7c4dff)" align="center" justify="center" color="white" fontWeight="900">{name[0]}</Flex>
        <Box><Text fontWeight="800" color="primary" fontSize="14px" noOfLines={1}>{name}</Text><Text fontSize="12px" color="gray.400">{members} członków</Text></Box>
      </HStack>
      <Flex align="center" justify="center" gap="7px" py="8px" borderRadius="11px" bg={btn.bg} color={btn.color} fontWeight="800" fontSize="14px"><Icon as={btn.icon} /> {btn.label}</Flex>
    </Box>
  );
};

// telefon + przeglądarka obok siebie: „w apce lub w webie"
const PhoneWebPair: FC<{ children: ReactNode }> = ({ children }) => (
  <HStack spacing="22px" align="center">
    <VStack spacing="8px">
      <Box w="150px" h="250px" borderRadius="26px" bg="#1b0140" p="9px" boxShadow="0 22px 50px rgba(67,0,152,0.28)">
        <Box w="100%" h="100%" borderRadius="18px" bg="#FBFAFF" overflow="hidden" display="flex" alignItems="center" justifyContent="center" p="10px">{children}</Box>
      </Box>
      <HStack spacing="6px" color="gray.500"><Icon as={FiSmartphone} /><Text fontSize="12px" fontWeight="700">W aplikacji</Text></HStack>
    </VStack>
    <VStack spacing="8px">
      <Box w="270px" borderRadius="14px" overflow="hidden" boxShadow="0 22px 50px rgba(67,0,152,0.20)" border="1px solid rgba(67,0,152,0.10)">
        <Flex align="center" gap="6px" px="11px" py="7px" bg="#f4f1fb"><Box w="8px" h="8px" borderRadius="full" bg="#ff5f57" /><Box w="8px" h="8px" borderRadius="full" bg="#febc2e" /><Box w="8px" h="8px" borderRadius="full" bg="#28c840" /></Flex>
        <Box bg="white" p="14px" h="200px" display="flex" alignItems="center" justifyContent="center">{children}</Box>
      </Box>
      <HStack spacing="6px" color="gray.500"><Icon as={FiGlobe} /><Text fontSize="12px" fontWeight="700">W przeglądarce</Text></HStack>
    </VStack>
  </HStack>
);

const WallMock: FC = () => (
  <Box w="360px" bg="white" borderRadius="18px" boxShadow="0 22px 54px rgba(67,0,152,0.18)" p="16px" border="1px solid rgba(67,0,152,0.08)">
    <HStack spacing="11px" mb="12px">
      <Flex w="46px" h="46px" borderRadius="13px" bg="linear-gradient(135deg,#430098,#7c4dff)" align="center" justify="center" color="white" fontWeight="900">R</Flex>
      <Box><Text fontWeight="900" color="primary">Rada Osiedla Kwiatowa</Text><Text fontSize="12px" color="gray.400">Ściana grupy</Text></Box>
    </HStack>
    <Box borderRadius="12px" bg="#faf8ff" p="12px" mb="10px">
      <HStack spacing="8px" mb="6px" color="gray.500" fontSize="12px"><Icon as={FiMessageSquare} /><Text>Nowy post</Text></HStack>
      <Text fontSize="13px" color="gray.700">Zapraszamy do głosowania nad nowym placem zabaw 🌳</Text>
    </Box>
    <Flex align="center" justify="space-between" borderRadius="12px" border="1px solid rgba(67,0,152,0.10)" p="12px">
      <HStack spacing="9px"><Icon as={FiBarChart2} color="accent" /><Text fontSize="13px" fontWeight="700" color="primary">Plac zabaw 2026</Text></HStack>
      <Box px="10px" py="3px" borderRadius="full" bg="rgba(47,206,37,0.14)" color="#1f9e1a" fontWeight="800" fontSize="12px">Trwa</Box>
    </Flex>
  </Box>
);

interface Beat { kicker?: string; title: ReactNode; text: string; visual: ReactNode; tone: "brand" | "light" }
const BEATS: Beat[] = [
  {
    title: <>Jak dołączyć do <chakra.span color="#FFD23F">grupy</chakra.span></>,
    text: "Krok po kroku, najprościej jak się da. Nie musisz się na niczym znać.",
    tone: "brand",
    visual: <Icon as={FiUsers} boxSize="90px" color="whiteAlpha.900" />
  },
  {
    kicker: "Na start",
    title: <>Są <chakra.span color="accent">dwa sposoby</chakra.span></>,
    text: "Albo ktoś przysłał Ci zaproszenie, albo znajdziesz grupę sam. Pokażemy oba.",
    tone: "light",
    visual: (
      <VStack spacing="14px" align="stretch">
        <HStack spacing="12px" bg="white" borderRadius="14px" p="14px" boxShadow="0 12px 30px rgba(67,0,152,0.10)"><Icon as={FiMail} color="accent" boxSize="24px" /><Text fontWeight="800" color="primary">Mam zaproszenie</Text></HStack>
        <HStack spacing="12px" bg="white" borderRadius="14px" p="14px" boxShadow="0 12px 30px rgba(67,0,152,0.10)"><Icon as={FiCompass} color="accent" boxSize="24px" /><Text fontWeight="800" color="primary">Szukam grupy sam</Text></HStack>
      </VStack>
    )
  },
  {
    kicker: "Sposób 1 · Zaproszenie",
    title: <>Dostajesz <chakra.span color="accent">link, kod QR albo e-mail</chakra.span></>,
    text: "Organizator wysyła zaproszenie na trzy sposoby. Każdy prowadzi do tej samej grupy.",
    tone: "light",
    visual: (
      <VStack spacing="14px">
        <LinkPill url="lubbezpośrednio.pl/grupy/zaproszenie" icon={FiLink} />
        <HStack spacing="10px"><Icon as={FiMail} color="accent" boxSize="22px" /><Text fontWeight="700" color="gray.600">albo e-mail z przyciskiem „Dołącz”</Text></HStack>
      </VStack>
    )
  },
  {
    kicker: "Zaproszenie · Kod QR",
    title: <>Zeskanuj kod <chakra.span color="accent">QR</chakra.span> telefonem</>,
    text: "Wyceluj aparatem telefonu w kod QR z zaproszenia. Otworzy się grupa — w aplikacji, jeśli ją masz, albo w przeglądarce.",
    tone: "light",
    visual: (
      <HStack spacing="20px" align="center">
        <QRMock />
        <Icon as={FiChevronRight} boxSize="30px" color="accent" />
        <VStack spacing="8px"><Icon as={FiSmartphone} boxSize="40px" color="primary" /><Text fontSize="13px" fontWeight="700" color="gray.500">otwiera grupę</Text></VStack>
      </HStack>
    )
  },
  {
    kicker: "Zaproszenie · Link / e-mail",
    title: <>Albo po prostu <chakra.span color="accent">kliknij link</chakra.span></>,
    text: "Kliknięcie linku z wiadomości lub e-maila otwiera grupę od razu. Działa tak samo w aplikacji i w przeglądarce.",
    tone: "light",
    visual: (
      <HStack spacing="16px" align="center">
        <LinkPill url="lubbezpośrednio.pl/grupy/zaproszenie" icon={FiLink} />
        <Icon as={FiChevronRight} boxSize="26px" color="accent" />
        <GroupCard name="Rada Osiedla Kwiatowa" members="128" state="member" w="200px" />
      </HStack>
    )
  },
  {
    kicker: "Sposób 2 · Szukam sam",
    title: <>Wejdź na <chakra.span color="accent">lubbezpośrednio.pl</chakra.span></>,
    text: "Nie masz zaproszenia? Otwórz naszą stronę w przeglądarce albo aplikację na telefonie i zaloguj się.",
    tone: "light",
    visual: <PhoneWebPair><VStack spacing="8px"><Icon as={FiLock} color="primary" boxSize="26px" /><Text fontSize="12px" fontWeight="800" color="primary">Zaloguj się</Text></VStack></PhoneWebPair>
  },
  {
    kicker: "Krok 1",
    title: <>Otwórz <chakra.span color="accent">Społeczność</chakra.span>, potem „Grupy”</>,
    text: "W menu kliknij „Społeczność”, a na górze przełącz na zakładkę „Grupy”. Zobaczysz publiczne grupy do dołączenia.",
    tone: "light",
    visual: (
      <VStack spacing="12px" align="center">
        <SidebarMock />
        <LinkPill url="lubbezpośrednio.pl/organizator/odkrywaj" icon={FiCompass} />
      </VStack>
    )
  },
  {
    kicker: "Krok 2",
    title: <>Kliknij <chakra.span color="accent">Dołącz</chakra.span></>,
    text: "Na karcie grupy naciśnij czerwony przycisk „Dołącz”. Zmieni się na „Należysz” — i już jesteś w środku.",
    tone: "light",
    visual: (
      <HStack spacing="16px" align="center">
        <GroupCard name="Rada Osiedla Kwiatowa" members="128" state="join" w="200px" />
        <Icon as={FiChevronRight} boxSize="26px" color="accent" />
        <GroupCard name="Rada Osiedla Kwiatowa" members="129" state="member" w="200px" />
      </HStack>
    )
  },
  {
    kicker: "Gotowe",
    title: <>Wchodzisz na <chakra.span color="accent">ścianę grupy</chakra.span></>,
    text: "Tu są posty członków, dyskusje, aktywne głosowania i lista ludzi. Możesz już głosować i komentować.",
    tone: "light",
    visual: <WallMock />
  },
  {
    title: <>Jesteś w <chakra.span color="#FFD23F">grupie!</chakra.span></>,
    text: "Głosuj, komentuj i decyduj razem z innymi — na telefonie i w przeglądarce.",
    tone: "brand",
    visual: <Text fontSize="24px" fontWeight="900" color="white">lubbezpośrednio.pl</Text>
  }
];

export const VsaasView: FC = () => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [idx, setIdx] = useState(0);

  const goTo = useCallback((i: number) => {
    const c = Math.max(0, Math.min(BEATS.length - 1, i));
    sectionRefs.current[c]?.scrollIntoView({ behavior: "smooth" });
    setIdx(c);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (["ArrowDown", "PageDown", " "].includes(e.key)) { e.preventDefault(); goTo(idx + 1); }
      else if (["ArrowUp", "PageUp"].includes(e.key)) { e.preventDefault(); goTo(idx - 1); }
    };
    window.addEventListener("keydown", onKey, { passive: false } as any);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, goTo]);

  return (
    <Box h="100vh" overflowY="auto" sx={{ scrollSnapType: "y mandatory", scrollBehavior: "smooth" }}>
      {BEATS.map((b, i) => {
        const brand = b.tone === "brand";
        return (
          <Flex
            key={i}
            ref={(el) => (sectionRefs.current[i] = el)}
            minH="100vh"
            align="center"
            justify="center"
            px={{ base: "24px", md: "72px" }}
            sx={{ scrollSnapAlign: "start" }}
            bg={brand ? "linear-gradient(150deg,#2d0066,#430098 60%,#5b12b8)" : "#FBFAFF"}
          >
            <Flex
              w="100%"
              maxW="1100px"
              direction={{ base: "column", md: i % 2 === 0 && !brand ? "row-reverse" : "row" }}
              align="center"
              justify="space-between"
              gap={{ base: "40px", md: "56px" }}
            >
              <MBox flex="1" variants={reveal} initial="hidden" whileInView="show" viewport={{ amount: 0.5 }}>
                {b.kicker && (
                  <HStack spacing="10px" mb="14px">
                    <Box w="26px" h="4px" borderRadius="full" bg="accent" />
                    <Text textTransform="uppercase" letterSpacing="0.16em" fontWeight="800" fontSize="15px" color="accent">{b.kicker}</Text>
                  </HStack>
                )}
                <Text fontSize={{ base: "32px", md: "50px" }} fontWeight="900" lineHeight="1.06" color={brand ? "white" : "primary"} mb="18px">{b.title}</Text>
                <Text fontSize={{ base: "17px", md: "20px" }} color={brand ? "whiteAlpha.900" : "gray.600"} maxW="540px" lineHeight="1.6">{b.text}</Text>
              </MBox>
              <MBox flex="1" display="flex" justifyContent="center" variants={revealD(0.12)} initial="hidden" whileInView="show" viewport={{ amount: 0.4 }}>
                {b.visual}
              </MBox>
            </Flex>
          </Flex>
        );
      })}
    </Box>
  );
};

export default VsaasView;
